import type { ReactNode } from "react";
import React from "react";
import { styled } from "@linaria/react";
import classNames from "classnames";
import { color, easingSoftMs, zIndex, easingBounce } from "../../ui";
import t from "../i18n";
import { HIDE_ON_EXPORT } from "../lib/downloadPage";
import {
  BANNER_HEIGHT_CSS_VARIABLE,
  PRIMARY_HEADER_HEIGHT_CSS_VARIABLE,
  SECONDARY_HEADER_HEIGHT_CSS_VARIABLE,
} from "../Header/useHeaderHeight";
import { ArrowLeftSFill } from "../IconV2/react";

export const SIDE_PANEL_WIDTH = 400;
export const SMALL_LAYOUT_WIDTH_PX = 700;
export const MEDIUM_LAYOUT_WIDTH_PX = 1000;
export const BIG_LAYOUT_WIDTH_PX = 1300;
export const SIDE_PANEL_OPENED_CN = "side-panel-opened";
const CLOSED_WIDTH = 20;
const OPENED_CN = "opened";
const HIDDEN = "hidden";
const HIDDEN_WIDTH = 0;
const SECONDARY_HEADER_PRESENT = "secondary-header";

const Arrow = styled(ArrowLeftSFill)`
  height: 30px;
  width: 30px;
`;

const IconWrapper = styled.div`
  @media print {
    display: none;
  }
  position: relative;
  user-select: none;
  cursor: pointer;
  top: 27px;
  right: 7px;

  height: 28px;
  width: 28px;
  border: 1px solid;
  border-color: ${color.titanium200};

  border-radius: 50%;
  z-index: ${zIndex.sidePanelButton};

  ${Arrow} {
    position: absolute;
    color: ${color.carbon800};
    top: -2px;
    left: -3px;
    transition: transform 350ms ${easingBounce};
  }

  &.${OPENED_CN} {
    ${Arrow} {
      transform: rotate(-180deg) translate(-2px, 0);
    }
  }
  &.${HIDDEN} {
    display: none;
  }

  background-color: ${color.hydrogen};
  :hover {
    border: 1px solid;
    border-color: ${color.lightNitrogen500_Main};
    background-color: ${color.lightNitrogen500_Main};
    ${Arrow} {
      color: ${color.hydrogen};
    }
  }
`;

const FixedWrapper = styled.div`
  position: fixed;
  z-index: ${zIndex.sidePanel};
  right: 0;
  top: calc(var(${PRIMARY_HEADER_HEIGHT_CSS_VARIABLE}) + var(${BANNER_HEIGHT_CSS_VARIABLE}, 0px));
  bottom: 0;

  &.${SECONDARY_HEADER_PRESENT} {
    top: calc(
      var(${PRIMARY_HEADER_HEIGHT_CSS_VARIABLE}) +
        var(${SECONDARY_HEADER_HEIGHT_CSS_VARIABLE}, 0px) + var(${BANNER_HEIGHT_CSS_VARIABLE}, 0px)
    );
  }
`;

const Wrapper = styled.div`
  position: absolute;

  background-color: ${color.hydrogen};
  border-left: 1px solid ${color.titanium200};

  display: flex;
  flex-direction: column;

  top: 0;
  bottom: 0;
  width: ${SIDE_PANEL_WIDTH}px;
  right: -${SIDE_PANEL_WIDTH - CLOSED_WIDTH}px;
  padding-left: ${CLOSED_WIDTH}px;
  cursor: pointer;
  transition: right ${easingSoftMs}ms;

  &.${OPENED_CN} {
    right: 0px;
    padding-left: 0px;
    cursor: initial;
  }

  &.${HIDDEN}:not(.${OPENED_CN}) {
    right: -${SIDE_PANEL_WIDTH}px;
    padding-left: ${HIDDEN_WIDTH}px;
  }

  @media (max-width: ${SIDE_PANEL_WIDTH}px) {
    width: 100%;
  }
  @media print {
    display: none;
    &.open {
      display: flex;
    }
  }
`;

interface P {
  header: ReactNode;
  opened: boolean;
  toggle: () => void;
  children: ReactNode;
  hiddenControls?: boolean;
  className?: string;
}

function SidePanel({ opened, toggle, header, children, hiddenControls = false, className }: P) {
  const IconButtonRef = React.useRef<HTMLDivElement>(null);
  const fixedWrapperRef = React.useRef<HTMLDivElement>(null);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const wrapper = fixedWrapperRef.current;
      if (!wrapper) throw new Error("Missing wrapper");
      if (document.activeElement === wrapper && opened && event.key === "Escape") toggle();
    },
    [opened, toggle],
  );

  const handleWrapperClick = React.useCallback(() => {
    if (!opened) toggle();
  }, [opened, toggle]);

  return (
    <FixedWrapper
      ref={fixedWrapperRef}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      className={className}
    >
      <IconWrapper
        ref={IconButtonRef}
        onClick={toggle}
        className={classNames(
          {
            [OPENED_CN]: opened,
            [HIDDEN]: hiddenControls,
          },
          HIDE_ON_EXPORT,
        )}
        title={opened ? t("side_panel.close") : t("side_panel.open")}
      >
        <Arrow />
      </IconWrapper>
      <Wrapper
        className={classNames({
          [OPENED_CN]: opened,
          [HIDDEN]: hiddenControls,
        })}
        onClick={handleWrapperClick}
        title={opened ? undefined : t("side_panel.open")}
      >
        {header}
        {opened ? children : null}
      </Wrapper>
    </FixedWrapper>
  );
}

export default React.memo(SidePanel);
